// src/components/Supplier/Supplier.jsx
// Main component for Supplier Management page

import React, { useState, useEffect } from 'react';
import supplierService from '../../services/supplierService';
import SupplierTable from './SupplierTable';
import SupplierModal from './SupplierModal';
import { showNotification } from '../../utils/notification';
import ConfirmDialog from "../common/ConfirmDialog";
import '../Inventory/inventory.css'; // reuse same CSS
import { PlusCircle } from 'lucide-react';

/**
 * Supplier Component
 * Main page component that manages suppliers
 * Handles CRUD operations and filtering
 */
const Supplier = ({ toggleSidebar }) => {
  // ========== STATE MANAGEMENT ==========
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [filters, setFilters] = useState({
    search: ''
  });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  //delete
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (supplier) => {
    setSupplierToDelete(supplier);
    setIsConfirmOpen(true);
  };

  // ========== LOAD DATA ON COMPONENT MOUNT ==========
  useEffect(() => {
    loadSuppliers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [suppliers, filters]);

  // ========== DATA LOADING ==========
  const loadSuppliers = async () => {
    try {
      setIsLoading(true);
      const data = await supplierService.getAll();
      setSuppliers(data);
      showNotification(`Loaded ${data.length} suppliers`, 'success');
    } catch (error) {
      console.error('Error loading suppliers:', error);
      showNotification(`Failed to load suppliers: ${error.message}`, 'error');
      setSuppliers([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ========== FILTERING ==========
  const applyFilters = () => {
    const list = Array.isArray(suppliers) ? suppliers : [];
    let filtered = [...list];

    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(sup => {
        const name = sup.name?.toLowerCase() || '';
        const contact = sup.contact?.toLowerCase() || '';
        const address = sup.address?.toLowerCase() || '';
        return (
          name.includes(searchLower) ||
          contact.includes(searchLower) ||
          address.includes(searchLower)
        );
      });
    }

    setFilteredSuppliers(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({ search: '' });
    showNotification('Filters cleared', 'info');
  };

  // ========== MODAL OPERATIONS ==========
  const handleAddNew = () => {
    setSelectedSupplier(null);
    setIsModalOpen(true);
  };

  const handleEdit = (supplier) => {
    setSelectedSupplier(supplier);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSupplier(null);
  };

  // ========== CRUD OPERATIONS ==========
  const handleSave = async (formData) => {
    try {
      setIsSaving(true);

      const payload = {
        name: formData.name,
        contact: formData.contact,
        address: formData.address,
        isActive: formData.isActive
      };

      if (selectedSupplier) {
        await supplierService.update(selectedSupplier.id, payload);
        showNotification(
          `Supplier "${payload.name}" updated successfully!`,
          'success'
        );
      } else {
        await supplierService.create(payload);
        showNotification(
          `New supplier "${payload.name}" added successfully!`,
          'success'
        );
      }

      await loadSuppliers();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving supplier:', error);
      showNotification(`Failed to save: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!supplierToDelete) return;

    try {
      setIsDeleting(true);
      await supplierService.delete(supplierToDelete.id); // API call

      showNotification("Supplier deleted successfully", "success");

      await loadSuppliers(); // refresh table
    } catch (error) {
      console.error("Delete failed:", error);
      showNotification(`Failed to delete: ${error.message}`, "error");
    } finally {
      setIsDeleting(false);
      setIsConfirmOpen(false);
      setSupplierToDelete(null);
    }
  };

  const cancelDelete = () => {
    setIsConfirmOpen(false);
    setSupplierToDelete(null);
  };

  // ========== PAGINATION LOGIC ==========
  const totalPages = Math.ceil(filteredSuppliers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSuppliers = filteredSuppliers.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // ========== RENDER ==========
  return (
    <main className="inventory-main-content">
      {/* Page Header */}
      <header className="inventory-content-header">
        <button onClick={toggleSidebar} className="sidebar-toggle-btn">
          ☰
        </button>
        <h2 className="inventory-content-title">Supplier Management</h2>
        <div className="header-actions">
          <button
            className="inventory-btn inventory-btn-add"
            onClick={handleAddNew}
          >
            <PlusCircle size={18} />
            Add New Supplier
          </button>
        </div>
      </header>

      {/* Filter Controls */}
      <section className="inventory-filter-controls">
        <div className="inventory-filter-group">
          <input
            type="text"
            name="search"
            className="inventory-custom-input inventory-search-input"
            placeholder="Search by Name, Contact or Address"
            value={filters.search}
            onChange={handleFilterChange}
          />
          {/* <button
            className="inventory-btn inventory-btn-primary"
            onClick={applyFilters}
          >
            Apply Filter
          </button> */}
          <button
            className="inventory-btn inventory-btn-clear"
            onClick={handleClearFilters}
          >
            Clear Filters
          </button>
        </div>
      </section>

      {/* Data Table */}
      <SupplierTable
        suppliers={paginatedSuppliers}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        isLoading={isLoading}
      />

      {/* Pagination Controls */}
      <div className="pagination-controls">
        <button 
          className="btn-page" 
          disabled={currentPage === 1} 
          onClick={handlePreviousPage}
        >
          Previous
        </button>
        
        <div className="page-numbers">
          {currentPage > 2 && (
            <>
              <button className="btn-page-num" onClick={() => handlePageChange(1)}>1</button>
              {currentPage > 3 && <span className="page-ellipsis">...</span>}
            </>
          )}
          
          {currentPage > 1 && (
            <button 
              className="btn-page-num" 
              onClick={() => handlePageChange(currentPage - 1)}
            >
              {currentPage - 1}
            </button>
          )}
          
          <button className="btn-page-num active">{currentPage}</button>
          
          {currentPage < totalPages && (
            <button 
              className="btn-page-num" 
              onClick={() => handlePageChange(currentPage + 1)}
            >
              {currentPage + 1}
            </button>
          )}
          
          {currentPage < totalPages - 1 && (
            <>
              {currentPage < totalPages - 2 && <span className="page-ellipsis">...</span>}
              <button className="btn-page-num" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
            </>
          )}
        </div>

        <span className="page-info">Page {currentPage} of {totalPages || 1}</span>
        
        <button 
          className="btn-page" 
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={handleNextPage}
        >
          Next
        </button>
      </div>

      {/* Add/Edit Modal */}
      <SupplierModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        supplier={selectedSupplier}
        isSaving={isSaving}
      />

      <ConfirmDialog
        isOpen={isConfirmOpen}
        title="Delete Supplier"
        message={`Are you sure you want to delete "${supplierToDelete?.name}"? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
      />
    </main>
  );
};

export default Supplier;